import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import './mobile-wish-list-product-thumbnail-style.scss';
import { useSnackbar } from '@src/providers/SnackbarProvider';
import useIsComDomain from '@src/hooks/useIsComDomain';

// Images & Icon
import CloseIcon from '@src/componentsNew/Icons/Icons/CloseIcon';
import MgsIcon from '@src/images/icons/mgs-logo.png';

// Components
import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import StarRatingIcon from '@src/componentsNew/Icons/StarRatingIcon';
import MobileAddToCartButton from '@src/componentsNew/Buttons/AddToCartButton/MobileAddToCartButton';

import { WISHLIST_DELETE } from '@src/pages/WishList/queries';
import { deleteWishlist } from '@src/pages/WishList/__generated__/deleteWishlist';
import { meForWishlist_me_wishlist_items_edges_node_product } from '@src/pages/WishList/__generated__/meForWishlist';

import { getPriceInfoFromProduct, formatPrice } from '@src/utils';
import { shouldShowElementAccordingToDomain, goToGlobalStore } from '@src/utils/domainCheck';

export interface IProps {
  wishlistProduct: meForWishlist_me_wishlist_items_edges_node_product;
  productId: string;
  refetch: any;
}

const MobileWishListThumbnail = ({ wishlistProduct, productId, refetch }: IProps) => {
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const isComDomain = useIsComDomain();

  const { originalPrice, finalPrice, isOnSale, discountPercent, isOutOfStock } = getPriceInfoFromProduct(
    wishlistProduct,
  );

  const [deleteWishList, { loading: deleteWishListLoading }] = useMutation<deleteWishlist>(WISHLIST_DELETE, {
    onCompleted: (data) => {
      showSnackbar({ message: `${wishlistProduct.name} removed from wishlist` });

      refetch();
    },
  });

  const [showVariants] = useState(wishlistProduct?.productType?.hasVariants && 1 < wishlistProduct?.variants?.length);

  const handleClickDeleteButton = (event) => {
    event.stopPropagation();

    deleteWishList({ variables: { productId } });
  };

  const handleClickThumbnail = (product) => {
    history.push(product.urlForGraphql);
  };

  return wishlistProduct ? (
    <div className="wish-list-product-thumbnail-wrapper" onClick={() => handleClickThumbnail(wishlistProduct)}>
      {/* 상품 이미지 영역 */}
      <div className="product-image-wrapper">
        <LazyLoadImage
          className="product-image"
          src={wishlistProduct?.thumbnail?.url}
          alt={wishlistProduct?.thumbnail?.alt}
          effect="blur"
        />
      </div>

      {/* 상품 정보 영역 */}
      <div className="product-description-wrapper">
        <div className="brand-name-delete-btn-wrapper">
          <Typography cName="brand-name" variant="subtitle2" component="span" style={{ color: '#090A0FDE' }}>
            {wishlistProduct.brand}
          </Typography>

          <CloseIcon cName="delete-btn" onClick={handleClickDeleteButton} />
        </div>

        <Typography cName="product-name" variant="body3" component="span" style={{ color: '#666666' }}>
          {wishlistProduct.name}
        </Typography>

        {showVariants || wishlistProduct.productType.isMgs ? (
          <div className="variants-mgs-wrapper">
            {showVariants && (
              <Typography variant="caption" component="span" style={{ color: '#11141D99' }}>
                {`${wishlistProduct?.variants?.length} Variants`}
              </Typography>
            )}
            {wishlistProduct.productType.isMgs && (
              <img className="mgs-logo-icon" src={MgsIcon} style={{ width: '3.3rem' }} />
            )}
          </div>
        ) : null}

        <div className="price-wrapper">
          <Typography variant="price" component="span" style={{ color: '#090A0FDE' }}>
            {formatPrice(finalPrice)}
          </Typography>
          {isOnSale && (
            <>
              <Typography variant="price" component="span" style={{ color: '#FF3399' }}>
                {discountPercent}%
              </Typography>
              <Typography variant="strikethrough2" component="span" style={{ color: '#11141D61' }}>
                {formatPrice(originalPrice)}
              </Typography>
            </>
          )}
        </div>

        {isOutOfStock && (
          <Typography cName="out-of-stock-badge" variant="body2" component="span" style={{ color: '#FFFFFF' }}>
            Out of stock
          </Typography>
        )}

        {wishlistProduct?.productReviewsAggregation?.rating ? (
          <div className="rating-area">
            <StarRatingIcon />
            <Typography variant="caption" component="span" style={{ color: '#212121' }}>
              {wishlistProduct?.productReviewsAggregation?.rating.toFixed(1)}
            </Typography>
            {wishlistProduct.productReviewsAggregation?.reviewCount ? (
              <Typography variant="caption" component="span" style={{ color: '#666666' }}>
                {`(${wishlistProduct.productReviewsAggregation?.reviewCount})`}
              </Typography>
            ) : null}
          </div>
        ) : null}

        {/* Domain Revert Comment */}
        {/* {
            shouldShowElementAccordingToDomain(isComDomain, wishlistProduct?.productType.isMgs) ?
              <MobileAddToCartButton
                product={wishlistProduct}
                refetch={refetch}
                meMobileNumber={meMobileNumber}
                meMobileVerified={meMobileVerified}
                loading={deleteWishListLoading}
              /> :
              (
                !isComDomain ?
                  <Button
                    data-text="Go to Global Store"
                    cName="go-global-store-btn"
                    variant="outlined"
                    size="large"
                    onClick={(e) => goToGlobalStore(e, false, wishlistProduct?.urlForGraphql)} />
                  : null
              )
          } */}
        <MobileAddToCartButton product={wishlistProduct} refetch={refetch} loading={deleteWishListLoading} />
      </div>
    </div>
  ) : null;
};

export default MobileWishListThumbnail;
